// src/pages/ChipSets.js
import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { auth, db } from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const COPPER = "#B87333";

function formatMoney(n) {
  const num = Number(n || 0);
  return num.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function sortChips(chips) {
  return (Array.isArray(chips) ? [...chips] : []).sort(
    (a, b) => Number(a.value) - Number(b.value)
  );
}

export default function ChipSets() {
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [loading, setLoading] = useState(true);
  const [chipSets, setChipSets] = useState([]);

  // create form
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  // editor selection
  const [selectedId, setSelectedId] = useState("");
  const selected = useMemo(
    () => chipSets.find((x) => x.id === selectedId) || null,
    [chipSets, selectedId]
  );

  // editor state
  const [editName, setEditName] = useState("");
  const [chips, setChips] = useState([]);
  const [chipValue, setChipValue] = useState("");
  const [chipColor, setChipColor] = useState("#B87333");

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // admin check + load list
  useEffect(() => {
    const run = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login");
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const admin = userSnap.exists() && userSnap.data()?.role === "admin";
        setIsAdmin(admin);
        setChecking(false);

        if (!admin) {
          navigate("/dashboard");
          return;
        }

        setLoading(true);
        const ref = collection(db, "chipSets");
        const q = query(ref, orderBy("name", "asc"));
        const snap = await getDocs(q);
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setChipSets(list);
        setLoading(false);
      } catch (err) {
        console.error("Load chipSets failed:", err);
        setLoading(false);
        setChecking(false);
        alert("Could not load chip sets (permissions?).");
      }
    };

    run();
  }, [navigate]);

  // when selecting a chip set, load into editor state
  useEffect(() => {
    if (!selected) {
      setEditName("");
      setChips([]);
      return;
    }
    setEditName(selected.name || "");
    setChips(sortChips(selected.chips || []));
  }, [selected]);

  const refreshList = async (selectId = null) => {
    const ref = collection(db, "chipSets");
    const q = query(ref, orderBy("name", "asc"));
    const snap = await getDocs(q);
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setChipSets(list);

    if (selectId) setSelectedId(selectId);
  };

  const createChipSet = async () => {
    if (!isAdmin) return;
    const nm = String(newName || "").trim();
    if (!nm) return alert("Enter a name for the chip set.");

    setCreating(true);
    try {
      const ref = collection(db, "chipSets");
      const created = await addDoc(ref, {
        name: nm,
        chips: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setNewName("");
      await refreshList(created.id);
      alert("Chip set created.");
    } catch (err) {
      console.error("Create chip set failed:", err);
      alert("Could not create chip set.");
    }
    setCreating(false);
  };

  const addChip = () => {
    const v = Number(chipValue);
    if (!v || v <= 0) return alert("Enter a valid chip value.");

    if (chips.some((c) => Number(c.value) === v)) {
      return alert("That chip value already exists in this set.");
    }

    const next = sortChips([
      ...chips,
      {
        value: v,
        label: `$${v.toLocaleString()}`,
        color: chipColor,
      },
    ]);

    setChips(next);
    setChipValue("");
  };

  const removeChip = (value) => {
    setChips((prev) => prev.filter((c) => Number(c.value) !== Number(value)));
  };

  const saveChipSet = async () => {
    if (!isAdmin) return;
    if (!selected) return;

    const nm = String(editName || "").trim();
    if (!nm) return alert("Name is required.");

    // minimal validation
    const cleaned = sortChips(chips).map((c) => ({
      value: Number(c.value),
      label: String(c.label || `$${Number(c.value).toLocaleString()}`),
      color: String(c.color || "#B87333"),
    }));

    setSaving(true);
    try {
      const ref = doc(db, "chipSets", selected.id);
      await updateDoc(ref, {
        name: nm,
        chips: cleaned,
        updatedAt: serverTimestamp(),
      });

      await refreshList(selected.id);
      alert("Chip set saved.");
    } catch (err) {
      console.error("Save chip set failed:", err);
      alert("Could not save chip set.");
    }
    setSaving(false);
  };

  const deleteChipSet = async () => {
    if (!isAdmin) return;
    if (!selected) return;

    const ok = window.confirm(
      `Delete "${selected.name}"? This cannot be undone.`
    );
    if (!ok) return;

    setDeleting(true);
    try {
      await deleteDoc(doc(db, "chipSets", selected.id));
      setSelectedId("");
      await refreshList(null);
      alert("Chip set deleted.");
    } catch (err) {
      console.error("Delete chip set failed:", err);
      alert("Could not delete chip set.");
    }
    setDeleting(false);
  };

  if (checking) {
    return (
      <Layout>
        <p className="text-zinc-300">Checking access...</p>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h2 className="text-3xl font-bold" style={{ color: COPPER }}>
              Chip Sets
            </h2>
            <p className="text-zinc-400 mt-1">
              Save chip denomination templates you can reuse for tournaments.
            </p>
          </div>

          <button
            onClick={() => navigate("/dashboard")}
            className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold"
          >
            Back
          </button>
        </div>

        {/* Create */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6">
          <h3 className="text-xl font-semibold mb-3" style={{ color: COPPER }}>
            Create New Chip Set
          </h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              className="flex-1 p-3 bg-zinc-800 text-zinc-100 rounded"
              placeholder="e.g. Cash Night Set, Tournament 1500 Set"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <button
              onClick={createChipSet}
              disabled={creating}
              className={`px-4 py-3 rounded font-semibold text-zinc-950 ${
                creating
                  ? "bg-[#B87333]/60 cursor-not-allowed"
                  : "bg-[#B87333] hover:bg-[#A46230]"
              }`}
            >
              {creating ? "Creating..." : "Create"}
            </button>
          </div>
        </div>

        {/* List + Editor */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            <h3 className="text-xl font-semibold mb-3" style={{ color: COPPER }}>
              Saved Chip Sets
            </h3>

            {loading ? (
              <p className="text-zinc-400">Loading...</p>
            ) : chipSets.length === 0 ? (
              <p className="text-zinc-400">No chip sets yet.</p>
            ) : (
              <div className="space-y-2">
                {chipSets.map((cs) => (
                  <button
                    key={cs.id}
                    onClick={() => setSelectedId(cs.id)}
                    className={`w-full text-left p-3 rounded border ${
                      selectedId === cs.id
                        ? "border-[#B87333] bg-black/30"
                        : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
                    }`}
                  >
                    <p className="text-zinc-100 font-semibold">{cs.name}</p>
                    <p className="text-zinc-400 text-sm">
                      {Array.isArray(cs.chips) ? cs.chips.length : 0} chip types
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Editor */}
          <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            {!selected ? (
              <div className="text-zinc-400">
                Select a chip set to edit.
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <label className="block text-zinc-300 text-sm mb-1">
                      Chip Set Name
                    </label>
                    <input
                      className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={saveChipSet}
                      disabled={saving}
                      className={`px-4 py-3 rounded font-semibold ${
                        saving
                          ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                          : "bg-green-700 hover:bg-green-800 text-white"
                      }`}
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>

                    <button
                      onClick={deleteChipSet}
                      disabled={deleting}
                      className={`px-4 py-3 rounded font-semibold ${
                        deleting
                          ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                          : "bg-red-600 hover:bg-red-700 text-white"
                      }`}
                    >
                      {deleting ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </div>

                {/* Add chip */}
                <div className="grid md:grid-cols-3 gap-3 items-end mb-5">
                  <div>
                    <label className="block text-zinc-300 text-sm mb-1">
                      Value
                    </label>
                    <input
                      type="number"
                      className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                      placeholder="e.g. 25"
                      value={chipValue}
                      onChange={(e) => setChipValue(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-zinc-300 text-sm mb-1">
                      Color
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        className="h-12 w-16 rounded bg-zinc-800 border border-zinc-700"
                        value={chipColor}
                        onChange={(e) => setChipColor(e.target.value)}
                      />
                      <input
                        type="text"
                        className="flex-1 p-3 bg-zinc-800 text-zinc-100 rounded"
                        value={chipColor}
                        onChange={(e) => setChipColor(e.target.value)}
                      />
                    </div>
                  </div>

                  <button
                    onClick={addChip}
                    className="px-4 py-3 rounded font-semibold text-zinc-950 bg-[#B87333] hover:bg-[#A46230]"
                  >
                    + Add Chip
                  </button>
                </div>

                {/* Chips grid */}
                {chips.length === 0 ? (
                  <p className="text-zinc-400">No chips added yet.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {sortChips(chips).map((c) => (
                      <div
                        key={c.value}
                        className="p-4 rounded border border-zinc-800 bg-zinc-950 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="h-10 w-10 rounded-full border border-zinc-700"
                            style={{ backgroundColor: c.color }}
                            title={c.color}
                          />
                          <div>
                            <p className="text-zinc-100 font-semibold">
                              {c.label || formatMoney(c.value)}
                            </p>
                            <p className="text-zinc-400 text-sm">
                              Value: {Number(c.value).toLocaleString()}
                            </p>
                          </div>
                        </div>

                        <button
                          onClick={() => removeChip(c.value)}
                          className="px-3 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-zinc-500 text-sm mt-5">
                  Tip: Keep 5–6 chip types for clean tournament math.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}